import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { API_URL } from './adminUtils';
import {
  Trash2,
  RefreshCw,
  Loader2,
  AlertTriangle,
} from 'lucide-react';

const DataManagement = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/data-stats`);
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load data stats');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleCleanup = async (type, label) => {
    if (!window.confirm(`Are you sure you want to delete ${label}? This action cannot be undone.`)) {
      return;
    }

    setDeleting(type);
    try {      let endpoint = '';
      
      switch (type) {
        case 'activities_all':
          endpoint = '/admin/cleanup/activities?status=all';
          break;
        case 'activities_pending':
          endpoint = '/admin/cleanup/activities?status=pending';
          break;
        case 'activities_rejected':
          endpoint = '/admin/cleanup/activities?status=rejected';
          break;
        case 'associations_unsigned':
          endpoint = '/admin/cleanup/associations/unsigned';
          break;
        case 'reset_demo':
          endpoint = '/admin/reset-demo-data';
          break;
        default:
          return;
      }

      const method = type === 'reset_demo' ? 'post' : 'delete';
      const response = await axios[method](`${API_URL}${endpoint}`, {});
      
      toast.success(response.data.message);
      fetchStats();
    } catch (error) {
      toast.error('Failed to perform cleanup');
    } finally {
      setDeleting(null);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-2xl font-bold">Data Management</h1>
        <p className="text-muted-foreground">View statistics and clean up unnecessary data</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-3xl font-bold">{stats?.associations || 0}</p>
            <p className="text-sm text-muted-foreground">Associations</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-3xl font-bold">{stats?.activities || 0}</p>
            <p className="text-sm text-muted-foreground">Total Activities</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-3xl font-bold text-yellow-600">{stats?.activities_pending || 0}</p>
            <p className="text-sm text-muted-foreground">Pending Activities</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-3xl font-bold text-red-600">{stats?.associations_unsigned || 0}</p>
            <p className="text-sm text-muted-foreground">Unsigned Associations</p>
          </CardContent>
        </Card>
      </div>

      {/* Cleanup Actions */}
      <Card className="border-orange-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-orange-600">
            <AlertTriangle className="w-5 h-5" />
            Data Cleanup Actions
          </CardTitle>
          <CardDescription>
            Use these actions to remove unnecessary or dummy data. These actions cannot be undone.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3">
            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <div>
                <p className="font-medium">Delete Pending Activities</p>
                <p className="text-sm text-muted-foreground">{stats?.activities_pending || 0} activities awaiting approval</p>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => handleCleanup('activities_pending', 'all pending activities')}
                disabled={deleting === 'activities_pending' || !stats?.activities_pending}
              >
                {deleting === 'activities_pending' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                Delete
              </Button>
            </div>

            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <div>
                <p className="font-medium">Delete Rejected Activities</p>
                <p className="text-sm text-muted-foreground">{stats?.activities_rejected || 0} rejected activities</p>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => handleCleanup('activities_rejected', 'all rejected activities')}
                disabled={deleting === 'activities_rejected' || !stats?.activities_rejected}
              >
                {deleting === 'activities_rejected' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                Delete
              </Button>
            </div>

            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <div>
                <p className="font-medium">Delete All Activities</p>
                <p className="text-sm text-muted-foreground">{stats?.activities || 0} total activities</p>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                className="text-red-600 hover:text-red-700"
                onClick={() => handleCleanup('activities_all', 'ALL activities')}
                disabled={deleting === 'activities_all' || !stats?.activities}
              >
                {deleting === 'activities_all' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                Delete All
              </Button>
            </div>

            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <div>
                <p className="font-medium">Delete Unsigned Associations</p>
                <p className="text-sm text-muted-foreground">{stats?.associations_unsigned || 0} associations without signatures</p>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => handleCleanup('associations_unsigned', 'all unsigned associations')}
                disabled={deleting === 'associations_unsigned' || !stats?.associations_unsigned}
              >
                {deleting === 'associations_unsigned' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                Delete
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Reset Demo Data */}
      <Card className="border-red-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="w-5 h-5" />
            Reset Demo Data
          </CardTitle>
          <CardDescription>
            This will delete all activities and reset all association signatures. Use this to start fresh.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button 
            variant="destructive"
            onClick={() => handleCleanup('reset_demo', 'all demo data and signatures')}
            disabled={deleting === 'reset_demo'}
          >
            {deleting === 'reset_demo' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
            Reset All Demo Data
          </Button>
        </CardContent>
      </Card>

      <Button variant="outline" onClick={fetchStats}>
        <RefreshCw className="w-4 h-4 mr-2" />
        Refresh Stats
      </Button>
    </div>
  );
};

export default DataManagement;
